import type { ZodValidationIssue } from "core/types";
import Translations from "translations/types";
import { ZodError, ZodIssue, z, type ZodTypeAny } from "zod";

/**
 * Generic Zod / form utilities shared across assessment, GKV, AHB, and other schemas.
 *
 * Attaches a validator that returns ZodValidationIssue[] to a base schema via superRefine.
 * Use when validation logic is expressed as (data) => issues[] rather than (data, ctx) => ctx.addIssue().
 */
export function createSimultaneousSchema<T extends ZodTypeAny>(
  baseSchema: T,
  validator: (data: z.infer<T>) => ZodValidationIssue[],
): T {
  return baseSchema.superRefine((data, ctx) => {
    const issues = validator(data);
    issues.forEach((issue) =>
      ctx.addIssue({ ...issue, code: z.ZodIssueCode.custom }),
    );
  });
}

export function flattenZodFieldErrorsDeep(
  error: ZodError,
): Record<string, string> {
  return Object.fromEntries(
    error.issues.map((e) => [e.path.join("."), e.message]),
  );
}

export function unflattenErrors(
  flatErrors: Record<string, string>,
): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [path, message] of Object.entries(flatErrors)) {
    const keys = path.split(".");
    let current = result;

    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = message;
      } else {
        current[key] = current[key] ?? {};
        current = current[key];
      }
    });
  }

  return result;
}

export function toNullishNumber(val: unknown) {
  if (typeof val === "string") {
    const t = val.trim();
    if (t === "") return null;
    const n = Number(t);
    return isNaN(n) ? val : n;
  }
  return val;
}

export function extractInvalidFields(issues: ZodIssue[]): string[] {
  return [
    ...new Set(
      issues.map((issue) => {
        const [group, ...rest] = issue.path;
        return rest.length > 0
          ? `${String(group)}.${rest.join(".")}`
          : String(group);
      }),
    ),
  ];
}

export const isFilled = (val: unknown) => {
  if (Array.isArray(val)) {
    if (val.length === 0) return false;
    return val.some(
      (v) =>
        (typeof v === "string" && v.trim().length > 0) ||
        typeof v === "number" ||
        typeof v === "boolean",
    );
  }

  if (typeof val === "string") {
    return val.trim().length > 0;
  }

  return val !== null && val !== undefined;
};

export function stripEmptyFields(data: unknown): Record<string, any> {
  // If not an object or completely empty, return an empty object
  if (
    !data ||
    typeof data !== "object" ||
    Array.isArray(data) ||
    !Object.values(data).some((value) => isFilled(value))
  ) {
    return {};
  }

  // Otherwise, keep only the filled values
  return Object.entries(data as Record<string, unknown>).reduce(
    (acc: Record<string, any>, [key, value]) => {
      if (isFilled(value)) {
        acc[key] = value;
      }
      return acc;
    },
    {},
  );
}

export const trimmedString = () => z.string().trim();

export function preprocessToOptionalNumber(val: unknown) {
  if (val === null || val === undefined || val === "") return undefined;
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const parsed = Number(val);
    return isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

export const createMaxLengthMessage = (
  translations: Translations,
  maxLength: number,
) => {
  return translations.patientForms.characterLimitValidation({
    numberMost: maxLength.toString(),
  });
};

export const createMinLengthMessage = (
  translations: Translations,
  minLength: number,
) => {
  return translations.patientForms.characterMinValidation({
    numberMinimum: minLength.toString(),
  });
};
